import "./css/WishButton.css";
import "./css/Desire.css";
import { useEffect, useMemo, useState } from "react";
import Title from "./components/Title";
import WishButton from "./components/WishButton";
import FoodList from "./components/FoodList";
import Desire from "./components/Desire";
import PopWishWindows from "./components/PopWishWindows";
import WishHistory from "./components/WishHistory";
import Category from "./components/Category";
import PreferenceBar from "./components/PreferenceBar";
import RecipeDetailModal from "./components/RecipeDetailModal";
import StartupRecommendationOverlay from "./components/StartupRecommendationOverlay";
import { categoryConfigs, defaultCategoryId, getCategoryConfig } from "./config/categories";
import { getStarColor, themeConfig } from "./config/theme";
import { getDailyTheme } from "./config/dailyThemes";
import { resolveWish } from "./logic/drawWish";
import { sortItemsByPreference } from "./logic/items";
import { createInitialSessions, getSessionByCategory, updateSessionByCategory, updateSessionTarget } from "./logic/session";
import { gachaConfig } from "./config/gacha";

function Wish() {
    const [itemsByCategory, setItemsByCategory] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(defaultCategoryId);
    const [sessions, setSessions] = useState(createInitialSessions);
    const [loading, setLoading] = useState(true);
    const [pendingTargetItem, setPendingTargetItem] = useState(null);
    const [showRetargetModal, setShowRetargetModal] = useState(false);
    const [preferencesByCategory, setPreferencesByCategory] = useState({});
    const [detailItem, setDetailItem] = useState(null);
    const [showStartupRecommendation, setShowStartupRecommendation] = useState(false);
    const [hasShownStartupRecommendation, setHasShownStartupRecommendation] = useState(false);

    const categoryConfig = getCategoryConfig(selectedCategory);
    const dailyTheme = useMemo(() => getDailyTheme(selectedCategory), [selectedCategory]);
    const currentSession = getSessionByCategory(sessions, selectedCategory);
    const currentItems = useMemo(() => itemsByCategory[selectedCategory] ?? [], [itemsByCategory, selectedCategory]);
    const currentPreference = preferencesByCategory[selectedCategory] ?? 'all';
    const sortedItems = useMemo(
        () => sortItemsByPreference(currentItems, currentPreference, dailyTheme),
        [currentItems, currentPreference, dailyTheme],
    );
    const startupRecommendationItems = useMemo(
        () => categoryConfigs
            .filter((category) => ['cooking', 'fitness'].includes(category.id))
            .flatMap((category) => (itemsByCategory[category.id] ?? []).map((item) => ({
                id: `${category.id}-${item.id}`,
                categoryId: category.id,
                categoryLabel: category.label,
                name: item.name,
                subtitle: item.subtitle,
                image: item.image,
                star: item.star,
                tags: item.tags ?? [],
                badge: item.cuisineBadge ?? item.cuisineName ?? category.label,
            }))),
        [itemsByCategory],
    );

    useEffect(() => {
        let mounted = true;
        const requests = categoryConfigs.map(async (category) => {
            const response = await fetch(category.dataSource);
            const data = await response.json();
            return [category.id, data];
        });

        Promise.all(requests)
            .then((entries) => {
                if (!mounted) {
                    return;
                }
                setItemsByCategory(Object.fromEntries(entries));
                setLoading(false);
            })
            .catch((error) => {
                console.error('Fetch error:', error);
                if (mounted) {
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (loading || hasShownStartupRecommendation || startupRecommendationItems.length === 0) {
            return;
        }

        setShowStartupRecommendation(true);
        setHasShownStartupRecommendation(true);
    }, [loading, hasShownStartupRecommendation, startupRecommendationItems]);

    function handleSelectCategory(categoryId) {
        setSelectedCategory(categoryId);
        setPendingTargetItem(null);
        setShowRetargetModal(false);
        setDetailItem(null);
    }

    function handleSelectPreference(preferenceId) {
        setPreferencesByCategory((prev) => ({
            ...prev,
            [selectedCategory]: preferenceId,
        }));
    }

    function handleWish() {
        const { result, star, nextSession, wasTargetHit } = resolveWish({
            items: currentItems,
            session: currentSession,
            targetItem: currentSession.selectItem,
        });

        setSessions((prev) => updateSessionByCategory(prev, selectedCategory, nextSession));

        return {
            result,
            star,
            wasTargetHit,
            countBeforeDraw: currentSession.count,
            targetItem: currentSession.selectItem,
            category: categoryConfig,
            maxDraws: gachaConfig.maxDraws,
        };
    }

    function handleItemSelect(item) {
        if (currentSession.selectItem) {
            setPendingTargetItem(item);
            setShowRetargetModal(true);
            return;
        }

        setSessions((prev) => updateSessionTarget(prev, selectedCategory, item));
    }

    function handleConfirmRetarget() {
        if (!pendingTargetItem) {
            return;
        }

        setSessions((prev) => updateSessionTarget(prev, selectedCategory, pendingTargetItem));
        setPendingTargetItem(null);
        setShowRetargetModal(false);
    }

    function handleCloseRetarget() {
        setPendingTargetItem(null);
        setShowRetargetModal(false);
    }

    function handleOpenDetail(item) {
        if (!item?.detailUrl) {
            return;
        }

        setDetailItem(item);
    }

    function handleCloseDetail() {
        setDetailItem(null);
    }

    function handleCloseStartupRecommendation() {
        setShowStartupRecommendation(false);
    }

    return (
        <div
            className="container background"
            style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/${categoryConfig.backgroundImage})`,
            }}
        >
            <Category
                categories={categoryConfigs}
                selectedCategory={selectedCategory}
                onSelectCategory={handleSelectCategory}
                jumpLink={themeConfig.jumpLink}
            />
            <div className="container wish-page">
                <section className="wish-hero-section">
                    <div className="all-div-box">
                        <Desire
                            item={currentSession.selectItem}
                            getColor={getStarColor}
                            emptyImage={themeConfig.defaultTargetImage}
                            emptyLabel={categoryConfig.emptyTargetLabel}
                            activeLabel={categoryConfig.activeTargetLabel}
                        />
                        <div className="title-button">
                            <Title
                                title={dailyTheme?.title ?? categoryConfig.title}
                                subtitle={dailyTheme?.subtitle ?? categoryConfig.subtitle}
                            />
                            <PreferenceBar
                                options={categoryConfig.preferenceOptions}
                                selectedPreference={currentPreference}
                                onSelectPreference={handleSelectPreference}
                            />
                            <WishButton
                                getColor={getStarColor}
                                onWish={handleWish}
                                onOpenDetail={handleOpenDetail}
                                wishLabel={categoryConfig.wishLabel}
                                progressCount={currentSession.count}
                                maxDraws={gachaConfig.maxDraws}
                                hasTarget={Boolean(currentSession.selectItem)}
                                loading={loading || currentItems.length === 0}
                                luckCopy={themeConfig.luckMeter[currentSession.mood]}
                                sessionSummary={dailyTheme?.title ?? categoryConfig.progressLabel}
                                totalDraws={currentSession.totalDraws ?? 0}
                                fiveStarStreak={currentSession.fiveStarStreak ?? 0}
                            />
                        </div>
                    </div>
                </section>
                <section className="wish-pool-section">
                    <FoodList
                        data={sortedItems}
                        getColor={getStarColor}
                        onSelectItem={handleItemSelect}
                        onOpenDetail={handleOpenDetail}
                        loading={loading}
                        selectedItem={currentSession.selectItem}
                    />
                </section>
                <PopWishWindows
                    showState={showRetargetModal}
                    currentItem={currentSession.selectItem}
                    nextItem={pendingTargetItem}
                    onConfirm={handleConfirmRetarget}
                    onCancel={handleCloseRetarget}
                    getColor={getStarColor}
                    title={themeConfig.retargetTitle}
                    description={themeConfig.retargetDescription}
                />
                <StartupRecommendationOverlay
                    open={showStartupRecommendation}
                    items={startupRecommendationItems}
                    getColor={getStarColor}
                    copy={themeConfig.startupRecommendation}
                    onClose={handleCloseStartupRecommendation}
                />
                <RecipeDetailModal item={detailItem} onClose={handleCloseDetail} />
                <section className="wish-history-section">
                    <WishHistory
                        wishHistory={currentSession.wishHistory}
                        getColor={getStarColor}
                        title={themeConfig.historyTitle}
                        emptyCopy={themeConfig.historyEmptyCopy}
                        lastResult={currentSession.lastResult}
                        wasTargetHit={currentSession.wasTargetHit}
                    />
                </section>
            </div>
        </div>
    );
}

export default Wish;
